import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { BASELINE } from "../util/index";
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from "framer-motion";
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode'; // ✅ 올바른 사용법
import { UserContext } from '../context/UserContext';
import Policy from '../Components/Policy';

const MIN_SIGNUP_TIME_MS = 4000;
const SIGNUP_PREMIUM_DAYS = 60;
const BLOCKED_EMAIL_DOMAINS = new Set([
  '10minutemail.com',
  '20minutemail.com',
  'mailinator.com',
  'guerrillamail.com',
  'guerrillamail.net',
  'sharklasers.com',
  'yopmail.com',
  'tempmail.com',
  'temp-mail.org',
  'throwawaymail.com',
  'trashmail.com',
  'dispostable.com',
  'getnada.com',
  'fakeinbox.com',
]);
const BLOCKED_EMAIL_DOMAIN_PARTS = [
  '10minutemail',
  'guerrillamail',
  'mailinator',
  'tempmail',
  'temp-mail',
  'throwawaymail',
  'trashmail',
  'yopmail',
  'dispostable',
  'fakeinbox',
];

const getEmailBlockReason = (email) => {
  const normalizedEmail = email.trim().toLowerCase();
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailPattern.test(normalizedEmail)) {
    return 'Please enter a valid email address.';
  }

  const domain = normalizedEmail.split('@')[1];
  const isBlockedDomain = BLOCKED_EMAIL_DOMAINS.has(domain)
    || BLOCKED_EMAIL_DOMAIN_PARTS.some((blockedPart) => domain.includes(blockedPart));

  if (isBlockedDomain) {
    return 'Please use a real email address. Temporary or disposable email services are not allowed.';
  }

  return '';
};

const SignupPanel = ({ onLogin }) => {
  const navigate = useNavigate();
  const { savelogin, saveCredentials } = useContext(UserContext);
  const location = useLocation();

  // ---------------------- [약관 동의] 관련 상태 ----------------------
  const [allAgreementsAccepted, setAllAgreementsAccepted] = useState(false);
  const [agreementLogs, setAgreementLogs] = useState({});
  const [data, setdata] = useState("");

  // Policy 컴포넌트가 모든 약관을 한 번에 { policy_1, policy_2, policy_3 } 로 전달
  const handlePolicyAgree = (policyData) => {
    setAgreementLogs(policyData);
    setAllAgreementsAccepted(true);
  };

  // ---------------------- [회원가입 폼] 관련 상태 ----------------------
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    grade: '0',
    role: 'student', // Default role set to 'student'
    couponCode: '',
  });
  const [fullName, setFullName] = useState('');
  const [isCouponValid, setIsCouponValid] = useState(null);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
  const [signupStartedAt] = useState(() => Date.now());
  const [isSignupReady, setIsSignupReady] = useState(false);
  const [botField, setBotField] = useState('');

  // ---------------------- [Google 관련] 상태 ----------------------
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('student'); // Default role set to 'student'

  // 이름 분리 함수
  const splitName = (fullName) => {
    if (!fullName || fullName.trim() === '') return { first: '', last: '' };

    const parts = fullName.trim().split(' ');

    if (parts.length === 1) {
      return { first: parts[0], last: '' };
    } else if (parts.length === 2) {
      return { first: parts[0], last: parts[1] };
    } else {
      // 3개 이상의 단어가 있는 경우, 마지막 단어를 성으로, 나머지를 이름으로
      const last = parts.pop();
      const first = parts.join(' ');
      return { first, last };
    }
  };

  // 이름이 변경될 때 first_name과 last_name 업데이트
  useEffect(() => {
    const { first, last } = splitName(fullName);
    setFormData(prev => ({
      ...prev,
      first_name: first,
      last_name: last
    }));
  }, [fullName]);

  useEffect(() => {
    const readyTimer = setTimeout(() => {
      setIsSignupReady(true);
    }, MIN_SIGNUP_TIME_MS);

    return () => clearTimeout(readyTimer);
  }, []);

  // 프리미엄 이용기간 계산 함수
  const getPremiumDates = () => {
    const today = new Date();
    const startPremiumDate = today.toISOString().split('T')[0];
    const endPremiumDate = new Date();
    endPremiumDate.setDate(endPremiumDate.getDate() + SIGNUP_PREMIUM_DAYS);
    const endPremiumDateString = endPremiumDate.toISOString().split('T')[0];
    return { startPremiumDate, endPremiumDateString };
  };

  // 인풋 값 변경 핸들러
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "fullName") {
      setFullName(value);
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleNewsletterChange = (event) => {
    setSubscribeNewsletter(event.target.checked);
  };

  const checkPasswordStrength = (password) => {
    const letterCount = (password.match(/[A-Za-z]/g) || []).length;
    const digitCount = (password.match(/\d/g) || []).length;
    return letterCount >= 5 && digitCount >= 3;
  };

  // 회원가입 제출 핸들러
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!allAgreementsAccepted) {
      alert('Please agree to all terms before signing up.');
      return;
    }

    if (formData.couponCode && isCouponValid === false) {
      alert('Please enter a valid coupon code or remove it.');
      return;
    }

    const emailBlockReason = getEmailBlockReason(formData.email);
    if (emailBlockReason) {
      alert(emailBlockReason);
      return;
    }

    const signupElapsedMs = Date.now() - signupStartedAt;
    if (signupElapsedMs < MIN_SIGNUP_TIME_MS) {
      alert('Please take a moment to review the form before creating your account.');
      return;
    }

    if (botField.trim() !== '') {
      alert('Unable to create account. Please try again.');
      return;
    }

    const { startPremiumDate, endPremiumDateString } = getPremiumDates();

    try {
      const dataToSend = {
        ...formData,
        email: formData.email.trim().toLowerCase(),
        premium: 1,
        start_premium_date: startPremiumDate,
        end_premium_date: endPremiumDateString,
        agreementLog: agreementLogs,
        signupElapsedMs,
        website: botField,
      };

      // 1) 서버로 회원가입 정보 전송
      await axios.post(BASELINE + 'user/insert/info', dataToSend);

      // 2) 뉴스레터 신청 (옵션)
      if (subscribeNewsletter) {
        await axios.post(BASELINE + 'email', [dataToSend.email]);
      }

      // 3) 환영 이메일 전송
      try {
        await axios.post(BASELINE + 'sendWelcomeEmail', {
          email: dataToSend.email,
          name: dataToSend.first_name,
        });
      } catch (error) {
        console.error('Failed to send welcome email:', error);
      }

      saveCredentials(dataToSend.email, formData.password);
      navigate('/');
    } catch (error) {
      if (error.response && error.response.status === 400) {
        alert(error.response.data.message || 'Email is already in use. Please use a different email.');
      } else {
        alert('An error occurred during registration. Please try again later.');
      }
    }
  };

  // 진행 단계 (Progress Bar) 계산
  const { email, password } = formData;
  let currentStep = 0;
  const isStep1Complete = fullName.trim() !== '';
  if (isStep1Complete) currentStep = 1;
  const isStep2Complete = isStep1Complete && email.trim() !== '';
  if (isStep2Complete) currentStep = 2;
  let isStep3Complete = false;
  isStep3Complete = isStep2Complete && checkPasswordStrength(password);
  if (isStep3Complete) currentStep = 3;
  const canSubmit = isStep3Complete && isSignupReady;
  const totalSteps = 3;
  const progressPercentage = (currentStep / totalSteps) * 100;

  const handleModalClose = () => {
    setShowRoleModal(false);
    setSelectedRole('');
  };
  // ==============================  
  // (1) Google Login - 성공 처리
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const token = credentialResponse.credential;
      const decoded = jwtDecode(token);
      const decodedUserData = {
        fullName: decoded.given_name + " " + decoded.family_name,
        email: decoded.email,
      };
      setdata(decodedUserData);
      setShowRoleModal(true);
    } catch (error) {
      console.log(error);
      alert('An error occurred during Google Sign-In registration. Please try again later.');
    }
  };

  // Google 로그인 최종 제출 함수 내에서 사용 예시
  const handleGoogleRoleSubmit = async () => {
    try {
      // fullName을 분리하여 first_name과 last_name 결정
      const { first, last } = splitName(data.fullName);
      const { startPremiumDate, endPremiumDateString } = getPremiumDates();

      const dataToSend = {
        first_name: first,
        last_name: last,
        email: data.email.trim().toLowerCase(),
        role: 'student', // 항상 student로 고정
        premium: 1,
        start_premium_date: startPremiumDate,
        end_premium_date: endPremiumDateString,
        from: 'google',
        agreementLog: agreementLogs,
        signupElapsedMs: Date.now() - signupStartedAt,
        website: botField,
      };
      await axios.post(BASELINE + 'user/insert/info', dataToSend);
      axios.post(BASELINE + 'user/login/google', dataToSend)
        .then((res) => {
          if (res.data.u_id !== '') {
            savelogin(res.data);
            onLogin();
            if (location.state?.from?.pathname) {
              navigate(location.state.from.pathname);
            } else {
              navigate("/");
            }
          } else {
            window.alert("Please double check the login information.");
            navigate("/");
          }
        })
        .catch(() => {
          window.alert("Please double check the login information.");
        });

      setShowRoleModal(false);
      setSelectedRole('');
    } catch (error) {
      console.log(error);
      alert('An error occurred during Google Sign-In registration. Please try again later.');
    }
  };


  // ==============================  
  // (3) Google Login - 실패 처리
  const handleGoogleFailure = (error) => {
    alert('Google Sign-In was unsuccessful. Please try again.');
  };

  return (
    <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-50 px-4 py-8">
      {/* Policy — shown before signup form */}
      {!allAgreementsAccepted && (
        <Policy onAgree={handlePolicyAgree} />
      )}

      {/* Signup form — shown after all policies accepted */}
      {allAgreementsAccepted && (
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-7 pb-5 border-b border-gray-100">
            <div className="flex justify-center mb-4">
              <a href="https://p-vault.com/dashboard">
                <img src="/dashboard/logoonly.png" alt="Vault Logo" className="h-9"
                  onError={e => { e.target.style.display = 'none'; }} />
              </a>
            </div>
            <h2 className="text-xl font-bold text-gray-900 text-center">Create your account</h2>
            <p className="text-sm text-gray-500 text-center mt-1">Welcome! Fill in your details below.</p>

            {/* Progress bar */}
            <div className="mt-5">
              <div className="flex items-center gap-1.5">
                {['Name', 'Email', 'Password'].map((label, i) => (
                  <React.Fragment key={label}>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                        currentStep > i ? 'bg-blue-600 text-white' :
                        currentStep === i ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-300' :
                        'bg-gray-100 text-gray-400'
                      }`}>
                        {currentStep > i ? '✓' : i + 1}
                      </div>
                      <span className={`text-xs font-medium transition-colors ${
                        currentStep >= i ? 'text-blue-600' : 'text-gray-400'
                      }`}>{label}</span>
                    </div>
                    {i < 2 && <div className={`flex-1 h-0.5 rounded transition-colors ${currentStep > i ? 'bg-blue-400' : 'bg-gray-200'}`} />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            <input type="hidden" name="role" value="student" />
            <input
              type="text"
              name="website"
              value={botField}
              onChange={(e) => setBotField(e.target.value)}
              autoComplete="off"
              tabIndex="-1"
              className="hidden"
              aria-hidden="true"
            />

            {/* Name */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide" htmlFor="fullName">
                Full Name *
              </label>
              <motion.input
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                className="border border-gray-200 rounded-xl w-full py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-400 transition-all"
                type="text" id="fullName" name="fullName" value={fullName}
                onChange={handleInputChange} placeholder="Jane Smith" required
              />
            </div>

            {/* Email */}
            {isStep1Complete && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide" htmlFor="email">
                  Email Address *
                </label>
                <input
                  className="border border-gray-200 rounded-xl w-full py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-400 transition-all"
                  type="email" id="email" name="email" value={formData.email}
                  onChange={handleInputChange} placeholder="jane@example.com" required
                />
              </motion.div>
            )}

            {/* Password + newsletter */}
            {isStep2Complete && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide" htmlFor="password">
                    Password *
                  </label>
                  <input
                    className="border border-gray-200 rounded-xl w-full py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 transition-all"
                    type="password" id="password" name="password"
                    value={formData.password} onChange={handleInputChange} required
                    placeholder="At least 5 letters + 3 digits"
                  />
                  <p className="mt-1.5 text-xs text-gray-400">Must contain ≥5 letters and ≥3 digits.</p>
                </div>

                <label className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    checked={subscribeNewsletter} onChange={handleNewsletterChange}
                  />
                  <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                    Get the latest newsletter & updates
                  </span>
                </label>

                <motion.button
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={!canSubmit}
                  className={`w-full py-3 rounded-xl text-sm font-bold transition-all duration-200 ${
                    canSubmit
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Create Account
                </motion.button>
                {!isSignupReady && (
                  <p className="text-center text-xs text-gray-400">
                    This button will be ready in a moment.
                  </p>
                )}
              </motion.div>
            )}
          </form>

          {/* Google Sign Up */}
          <div className="px-6 pb-6">
            <div className="relative flex items-center my-4">
              <div className="flex-1 border-t border-gray-200" />
              <span className="px-3 text-xs text-gray-400 font-medium">or continue with</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>
            <GoogleOAuthProvider clientId="208805948909-68s39ohls7juea9hmou6l11sbpvt0pms.apps.googleusercontent.com">
              <div className="flex justify-center">
                <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleFailure} useOneTap text="signup_with" shape="pill" />
              </div>
            </GoogleOAuthProvider>
            <p className="text-center text-xs text-gray-400 mt-4">
              Already have an account?{' '}
              <a href="/dashboard/login" className="text-blue-500 hover:underline font-medium">Sign in</a>
            </p>
          </div>
        </div>
      )}

      {showRoleModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-xs relative"
          >
            <button
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all"
              onClick={handleModalClose}
            >
              ✕
            </button>
            <h2 className="text-lg font-bold text-gray-900 mb-1 text-center">Choose your role</h2>
            <p className="text-xs text-gray-500 text-center mb-4">How will you be using P-Vault?</p>
            <select
              className="border border-gray-200 rounded-xl w-full py-3 px-4 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="">— Select a role —</option>
              <option value="student">Student</option>
            </select>
            <button
              onClick={handleGoogleRoleSubmit}
              className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-all active:scale-95"
            >
              Confirm
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SignupPanel;